import classNames from "classnames";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Badge } from "reactstrap";
import { Icon, UserAvatar } from "../../../components/Component";
import { serviceGetUsersByUserId } from "../../../services/users";
import { igGetProfile } from "../../../services/igAdapter";
import getTextColor from "../../../components/custom/getTextColor";
import { IgChatContext } from "./IgChatContext";
import avatarPlaceholder from "../../../images/default-avatar.webp";

/**
 * IgChatAsideBody — render satu item conversation di sidebar Instagram.
 *
 * Props:
 *   item: ChatResponse
 *   onSelect: function(item)
 */
const IgChatAsideBody = ({ item, onSelect }) => {
  const { selectedIdState, departmentMapState } = useContext(IgChatContext);
  const [departmentMap] = departmentMapState;
  const [selectedId] = selectedIdState;
  const [, setHover] = useState(false);
  const [cookies] = useCookies(["SIDikey", "departmentIds"]);
  const [assignedName, setAssignedName] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    if (!item?.participant_user_id || !item?.account_slug) return;
    igGetProfile(item.account_slug, item.participant_user_id).then((res) => {
      if (res?.data?.avatarUrl) setAvatarUrl(res.data.avatarUrl);
    });
  }, [item?.participant_user_id, item?.account_slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!item?.assigned_user_id) {
      setAssignedName(null);
      return;
    }
    serviceGetUsersByUserId(cookies.SIDikey, item.assigned_user_id, cookies.departmentIds).then((res) => {
      const user = res?.data?.data?.[0];
      if (user) {
        const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
        setAssignedName(name || "CS Instagram");
      } else {
        setAssignedName("CS Instagram");
      }
    });
  }, [item?.assigned_user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = selectedId === item.conversation_id;
  const displayName = item.participant_username || item.participant_user_id || "Unknown";
  const previewText = item.latest_message_text || "—";
  const timeLabel = item.updated_at
    ? moment(item.updated_at).isSame(moment(), "day")
      ? moment(item.updated_at).format("HH:mm")
      : moment(item.updated_at).format("DD/MM")
    : "";

  // Attachment icon jika pesan bukan teks
  const isAttachment = !item.latest_message_text && item.latest_message_id;
  const attachmentLabel = isAttachment ? "[Attachment]" : null;

  const unreadCount = item.unread_count || 0;
  // ticket_status berasal dari tabel tickets crm-backend (via cache crm-ig),
  // beda dari unreadCount — jangan pakai unreadCount buat nentuin OPENED,
  // itu cuma penanda "sudah dibaca", bukan status tiket.
  const isTicketOpened = item.ticket_status === "OPENED";

  // Warna avatar konsisten per username

  return (
    <li
      className={classNames("chat-item", "w-100", { "is-unread": unreadCount > 0 })}
      style={{ backgroundColor: isActive ? "#fafafa" : "" }}
    >
      <a
        className="chat-link"
        href="#chat-link"
        onClick={(e) => {
          e.preventDefault();
          onSelect(item);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <UserAvatar image={avatarUrl || avatarPlaceholder} className="chat-media" />

        <div className="chat-info">
          {/* Row 1: Nama + Waktu */}
          <div className="chat-from">
            <div className="name text-truncate pr-2">@{displayName}</div>
            <span className="time">{timeLabel}</span>
          </div>

          {/* Row 2: Preview pesan + badge assigned */}
          <div className="chat-context custom-chat-context">
            <div className="text">
              <div className="mb-1 d-inline-flex w-100">
                {isAttachment && (
                  <span style={{ marginRight: 5 }}>
                    <Icon name="img-fill" className="text-soft" style={{ fontSize: 13 }} />
                  </span>
                )}
                <p className="text-truncate pr-2 font-weight-light">
                  {attachmentLabel || (previewText.length > 50 ? previewText.slice(0, 50) + "…" : previewText)}
                </p>
              </div>
            </div>

            {/* Badge: unread count */}
            {unreadCount > 0 && (
              <div className="position-relative mr-1">
                <Badge color="danger" pill>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Badge>
              </div>
            )}

            <span
              className={`badge border-2 rounded-pill ${isTicketOpened ? "border-blue text-blue" : "border-red text-red"}`}
              style={{ fontSize: 10, fontWeight: 800 }}
            >
              {isTicketOpened ? "OPENED" : "UNOPENED"}
            </span>
          </div>

        </div>
      </a>

      {/* Row 3: Badge department + akun + assigned user — di luar <a> agar tidak terkena flex constraint */}
      <div
        onClick={(e) => { e.preventDefault(); onSelect(item); }}
        style={{ padding: "0 0.75rem 0.5rem 4.25rem", cursor: "pointer", display: "flex", flexWrap: "nowrap", gap: 6, overflowX: "auto", msOverflowStyle: "none", scrollbarWidth: "none" }}
      >
        {item.department_id && departmentMap[item.department_id] && (
          <span
            className="badge rounded-pill"
            style={{
              fontSize: 10,
              fontWeight: 800,
              backgroundColor: departmentMap[item.department_id].color || "#6c757d",
              color: getTextColor(departmentMap[item.department_id].color || "#6c757d"),
            }}
          >
            {departmentMap[item.department_id].name}
          </span>
        )}
        {!item.department_id && !item.channel_id && (
          <span
            className="badge rounded-pill text-white text-uppercase"
            style={{ fontSize: 10, fontWeight: 800, backgroundColor: "#049b1c" }}
          >
            {item.account_slug}
          </span>
        )}
        {assignedName && (
          <span
            className="badge rounded-pill text-dark"
            style={{ fontSize: 10, fontWeight: 800, backgroundColor: "#f7f5f5" }}
          >
            {assignedName}
          </span>
        )}
      </div>
    </li>
  );
};

export default IgChatAsideBody;
